import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getBusiness, getMembers } from './businessApi';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];
const DAY_LABEL = { nl: { mon:'Ma',tue:'Di',wed:'Wo',thu:'Do',fri:'Vr',sat:'Za',sun:'Zo' }, en: { mon:'Mon',tue:'Tue',wed:'Wed',thu:'Thu',fri:'Fri',sat:'Sat',sun:'Sun' } };

export default function BusinessProfileScreen({ lang, businessId, isOwner, onEdit }) {
  const nl = lang === 'nl';
  const [biz, setBiz] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!businessId) { setLoading(false); return; }
    Promise.all([getBusiness(businessId), getMembers(businessId)])
      .then(([b, m]) => { setBiz(b); setMembers(Array.isArray(m) ? m : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [businessId]);

  if (loading) return <TechLoadingBar lang={lang} />;
  if (!biz || biz.error) return (
    <div style={{ textAlign: 'center', padding: 40, color: C.silver }}>{nl ? 'Bedrijfsprofiel niet gevonden.' : 'Business profile not found.'}</div>
  );

  const hours = biz.opening_hours || {};
  const activeMembers = members.filter(m => m.status === 'active');

  return (
    <div style={{ padding: '0 0 24px' }}>
      <style>{CSS}</style>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg,${_T.bg3},${_T.bg4})`, border: `1px solid ${_T.cardBorderTop}`, borderRadius: 20, padding: 20, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {biz.logo_url
            ? <img src={biz.logo_url} alt="" style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover', border: `2px solid ${_T.accent}44`, flexShrink: 0 }} />
            : <div style={{ width: 64, height: 64, borderRadius: 16, background: `${_T.accent}20`, border: `2px solid ${_T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>🏢</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: C.white, fontWeight: 700 }}>{biz.name}</div>
            {biz.address && <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>📍 {biz.address}</div>}
            <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>👥 {activeMembers.length} {nl ? 'medewerkers' : 'members'}</div>
          </div>
        </div>
        {biz.description && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${_T.cardBorderBot}`, color: C.silver2, fontSize: 13, lineHeight: 1.6 }}>{biz.description}</div>
        )}
        {isOwner && onEdit && (
          <button onClick={onEdit} style={{ marginTop: 12, width: '100%', background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '9px', cursor: 'pointer' }}>
            ✏️ {nl ? 'Profiel bewerken' : 'Edit profile'}
          </button>
        )}
      </div>

      {/* Opening hours */}
      {Object.values(hours).some(v => v) && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>{nl ? 'OPENINGSTIJDEN' : 'OPENING HOURS'}</div>
          {DAYS.map(d => (
            <div key={d} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: C.silver, fontSize: 12 }}>{(nl ? DAY_LABEL.nl : DAY_LABEL.en)[d]}</span>
              <span style={{ color: hours[d] ? C.white : C.silver, fontSize: 12, fontWeight: hours[d] ? 600 : 400 }}>
                {hours[d] || (nl ? 'Gesloten' : 'Closed')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Team */}
      {activeMembers.length > 0 && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px' }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>{nl ? 'TEAM' : 'TEAM'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {activeMembers.slice(0, 12).map(m => {
              const ac = m.role === 'owner' ? '#C8A84B' : C.silver;
              return (
                <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: `${ac}10`, border: `1px solid ${ac}33`, borderRadius: 20, padding: '4px 10px 4px 4px' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${ac}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: ac }}>
                    {(m.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span style={{ color: C.silver2, fontSize: 11 }}>{m.name}</span>
                  {m.role === 'owner' && <span style={{ fontSize: 9 }}>👑</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
